/**
 * ============================================================================
 * KbPageActionsMenu — Dropdown de ações do documento (kebab do header).
 * ----------------------------------------------------------------------------
 * Substitui os botões antigos "Salvar rascunho / Enviar para revisão /
 * Publicar / Reindexar / Embed" que ocupavam o header do editor. Concentra
 * tudo em um único menu (padrão GitBook).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { MoreVertical, Save, Send, Rocket, RefreshCw, Sparkles, Link2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

interface Props {
  status: string;
  canEdit: boolean;
  canPublish: boolean;
  hasChunks: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isSubmittingReview: boolean;
  isPublishing: boolean;
  isReindexing: boolean;
  isEmbedding: boolean;
  onSaveDraft: () => void;
  onSubmitReview: () => void;
  onPublish: () => void;
  onReindex: () => void;
  onEmbed: () => void;
  onOpenGovernance: () => void;
}

/**
 * Menu kebab com todas as ações do documento. Itens habilitados conforme
 * status (draft/in_review/approved/published) e permissões do usuário.
 */
export function KbPageActionsMenu(props: Props) {
  const {
    status, canEdit, canPublish, hasChunks, isDirty,
    isSaving, isSubmittingReview, isPublishing, isReindexing, isEmbedding,
    onSaveDraft, onSubmitReview, onPublish, onReindex, onEmbed, onOpenGovernance,
  } = props;

  const isDraft = status === "draft";
  const isApproved = status === "approved";
  const isPublished = status === "published";

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Ações do documento">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Ações do documento</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Edição — visível apenas em rascunho */}
        {isDraft && canEdit && (
          <>
            <DropdownMenuItem onSelect={onSaveDraft} disabled={!isDirty || isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Salvando…" : "Salvar rascunho"}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSubmitReview} disabled={isSubmittingReview}>
              <Send className="h-4 w-4 mr-2" />
              Enviar para revisão
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Publicação — visível apenas quando aprovado */}
        {isApproved && canPublish && (
          <>
            <DropdownMenuItem onSelect={onPublish} disabled={isPublishing}>
              <Rocket className="h-4 w-4 mr-2" />
              Publicar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Indexação — visível apenas quando publicado */}
        {isPublished && (
          <>
            <DropdownMenuItem onSelect={onReindex} disabled={isReindexing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isReindexing ? "animate-spin" : ""}`} />
              Reindexar chunks
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onEmbed}
              disabled={isEmbedding || !hasChunks}
            >
              <Sparkles className={`h-4 w-4 mr-2 ${isEmbedding ? "animate-pulse" : ""}`} />
              Gerar embeddings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Governança */}
        <DropdownMenuItem onSelect={onOpenGovernance}>
          <Settings className="h-4 w-4 mr-2" />
          Governança &amp; taxonomias
        </DropdownMenuItem>

        {/* Utilitários gerais */}
        <DropdownMenuItem onSelect={handleCopyLink}>
          <Link2 className="h-4 w-4 mr-2" />
          Copiar link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
